/**
 * Main App component with Refine configuration
 *
 * Routes and resources are generated dynamically from module manifests.
 * To hide a module for a customer, set navigation.hidden = true in its manifest.
 */

import React from "react";
import { Refine } from "@refinedev/core";
import { useNotificationProvider } from "@refinedev/antd";
import { customTheme } from "@/theme";
import routerProvider, {
  NavigateToResource,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from "@refinedev/react-router-v6";
import { Authenticated } from "@refinedev/core";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { ConfigProvider, App as AntdApp } from "antd";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { createMCPDataProvider } from "@/providers/mcpDataProvider";
import { authProvider } from "@/providers/authProvider";
import { CRMLayout } from "@/components/layout/CRMLayout";

// Dynamic route generation from module registry
import {
  getRefineResources,
  getModuleRoutes,
} from "@/modules/entities/routeConfig";

// Pages (Dashboard and Login are always available)
import { Dashboard } from "@/pages/dashboard";
import { LoginPage } from "@/pages/login";

// Configure React Query with optimizations for speed
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

// API URL - will be proxied to backend
const API_URL = "/api";

// Create data provider
const dataProvider = createMCPDataProvider(API_URL);

// Generate resources dynamically from module registry
const resources = getRefineResources();

const App: React.FC = () => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ConfigProvider theme={customTheme}>
          <AntdApp>
            <Refine
              routerProvider={routerProvider}
              dataProvider={dataProvider}
              authProvider={authProvider}
              notificationProvider={useNotificationProvider}
              resources={resources}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
              }}
            >
              <Routes>
                {/* Login route - not protected */}
                <Route path="/login" element={<LoginPage />} />

                {/* Protected routes */}
                <Route
                  element={
                    <Authenticated
                      key="authenticated-routes"
                      fallback={<LoginPage />}
                    >
                      <CRMLayout>
                        <Outlet />
                      </CRMLayout>
                    </Authenticated>
                  }
                >
                  <Route index element={<Dashboard />} />

                  {/* Dynamic module routes - generated from registry */}
                  {getModuleRoutes()}

                  {/* Catch all */}
                  <Route
                    path="*"
                    element={<NavigateToResource resource="dashboard" />}
                  />
                </Route>
              </Routes>

              <UnsavedChangesNotifier />
              <DocumentTitleHandler
                handler={({ pathname, resource }) => {
                  const baseTitle = "evo-infinito";
                  if (resource?.name) {
                    const resourceName = resource.meta?.label || resource.name;
                    return `${resourceName} | ${baseTitle}`;
                  }
                  if (pathname === "/") {
                    return `Dashboard | ${baseTitle}`;
                  }
                  return baseTitle;
                }}
              />
            </Refine>
          </AntdApp>
        </ConfigProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </BrowserRouter>
  );
};

export default App;
